import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MapPin, Briefcase, Calendar, Search, CalendarClock } from 'lucide-react';
import CareerLayout from './CareerLayout';
import { getTenantHeader, listJobs } from '../../services/careerService';

const PageTitle = styled.h1`
  font-size: 28px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 24px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const SearchBox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 260px;
  min-width: 220px;
  background: #fff;
  border: 1px solid #E0E4E8;
  border-radius: 8px;
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${({ theme }) => theme.metrics.font.family};
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#E0E4E8')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#fff')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.dark.little)};
  border-radius: 999px;
  padding: 6px 14px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const Card = styled(Link)`
  display: block;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 20px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 66, 66, 0.08);
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const CardTitle = styled.h3`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Empty = styled.div`
  background: #fff;
  border: 1px dashed #D5DCE3;
  border-radius: 10px;
  padding: 48px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const fmtDate = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(lang || 'tr'); } catch { return null; }
};

const ALL = '__all__';

// Küçük/büyük harf ve Türkçe noktalı/noktasız i farkını yutan arama normalizasyonu.
// (tr-TR CompareInfo KULLANILMAZ: orada büyük "I" noktasız "ı"ya katlanır ve
// "WILLIAM"/"William" ayrışır — bkz. AtsPersonIdentity / NormalizeNameForMatch.)
const normalize = (s) => (s || '')
  .replace(/[İIıi]/g, 'i')
  .toLocaleLowerCase('en')
  .trim();

export default function CareerJobListPage() {
  const { tenantSlug } = useParams();
  const { t, i18n } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // FE-22 — "şirket yok" ile "yüklenemedi" AYRI durumlardır. Eskiden her hata
  // "Şirket sayfası bulunamadı" diyordu ve altına ham hata metni basılıyordu.
  const [loadFailed, setLoadFailed] = useState(false);

  const [term, setTerm] = useState('');
  const [department, setDepartment] = useState(ALL);
  const [location, setLocation] = useState(ALL);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setLoadFailed(false);
    Promise.all([getTenantHeader(tenantSlug), listJobs(tenantSlug)])
      .then(([tRes, jRes]) => {
        if (cancelled) return;
        const tData = tRes.data?.data;
        const jData = jRes.data?.data;
        if (!tData) {
          // PUBLIC SAYFA: teşhis YALNIZ konsola. Ziyaretçiye hiçbir ham yanıt,
          // HTTP durumu ya da iç alan adı gösterilmez (FE-22).
          // eslint-disable-next-line no-console
          console.warn('[Career] empty tenant payload', tRes.data);
          setNotFound(true);
          return;
        }
        setTenant(tData);
        setJobs(Array.isArray(jData) ? jData : []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[Career] fetch failed', err);
        if (cancelled) return;
        // Kariyer istemcisinde interceptor YOKTUR (careerService), bu yüzden
        // gerçek HTTP durumu burada okunabilir: 404 "şirket yok", gerisi
        // "yüklenemedi" — ikisi ziyaretçiye AYNI şeyi söylememeli.
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantSlug]);

  const departments = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.department).filter(Boolean))).sort(),
    [jobs],
  );
  const locations = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))).sort(),
    [jobs],
  );

  // Süzme TAMAMEN istemcide: public uç ilanların tamamını tek seferde döner
  // (yayında + süresi dolmamış), ayrı bir arama ucu yok.
  const visibleJobs = useMemo(() => {
    const needle = normalize(term);
    return jobs.filter((j) => {
      if (department !== ALL && j.department !== department) return false;
      if (location !== ALL && j.location !== location) return false;
      if (!needle) return true;
      return normalize(j.title).includes(needle)
        || normalize(j.department).includes(needle)
        || normalize(j.location).includes(needle);
    });
  }, [jobs, term, department, location]);

  if (loading) {
    return (
      <CareerLayout tenant={null}>
        <PageTitle>{t('career.loading')}</PageTitle>
      </CareerLayout>
    );
  }

  if (notFound || loadFailed) {
    return (
      <CareerLayout tenant={null}>
        <Empty>{notFound ? t('career.tenantNotFound') : t('career.loadFailed')}</Empty>
      </CareerLayout>
    );
  }

  const hasFilters = departments.length > 1 || locations.length > 1 || jobs.length > 4;

  return (
    <CareerLayout tenant={tenant}>
      <PageTitle>{t('career.openPositions')}</PageTitle>
      <PageSubtitle>{t('career.openPositionsSubtitle', { count: jobs.length })}</PageSubtitle>

      {jobs.length === 0 ? (
        <Empty>{t('career.noJobs')}</Empty>
      ) : (
        <>
          {hasFilters && (
            <Toolbar>
              <SearchBox>
                <Search size={16} />
                <input
                  type="search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t('career.searchPlaceholder')}
                  aria-label={t('career.searchPlaceholder')}
                />
              </SearchBox>
              {departments.length > 1 && (
                <ChipRow>
                  <Chip type="button" $active={department === ALL} onClick={() => setDepartment(ALL)}>
                    {t('career.filter.allDepartments')}
                  </Chip>
                  {departments.map((d) => (
                    <Chip key={d} type="button" $active={department === d} onClick={() => setDepartment(d)}>{d}</Chip>
                  ))}
                </ChipRow>
              )}
              {locations.length > 1 && (
                <ChipRow>
                  <Chip type="button" $active={location === ALL} onClick={() => setLocation(ALL)}>
                    {t('career.filter.allLocations')}
                  </Chip>
                  {locations.map((l) => (
                    <Chip key={l} type="button" $active={location === l} onClick={() => setLocation(l)}>{l}</Chip>
                  ))}
                </ChipRow>
              )}
            </Toolbar>
          )}

          {visibleJobs.length === 0 ? (
            <Empty>{t('career.noMatchingJobs')}</Empty>
          ) : (
            <Grid>
              {visibleJobs.map((j) => (
                <Card key={j.id} to={`/career/${tenantSlug}/jobs/${j.slug}`}>
                  <CardTitle>{j.title}</CardTitle>
                  <Meta>
                    {j.department && (
                      <MetaItem><Briefcase size={14} />{j.department}</MetaItem>
                    )}
                    {j.location && (
                      <MetaItem><MapPin size={14} />{j.location}</MetaItem>
                    )}
                    {j.postedAt && (
                      <MetaItem><Calendar size={14} />{fmtDate(j.postedAt, i18n.language)}</MetaItem>
                    )}
                    {j.expiresAt && (
                      <MetaItem>
                        <CalendarClock size={14} />
                        {t('career.expiresAt', { date: fmtDate(j.expiresAt, i18n.language) })}
                      </MetaItem>
                    )}
                  </Meta>
                </Card>
              ))}
            </Grid>
          )}
        </>
      )}
    </CareerLayout>
  );
}
